import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { resetAttractionImage } from '~/services/AttractionsService';

import type { Attraction } from '~/types/attractionTypes';

import { setCacheBuster } from './attractionCacheBuster';
import { attractionQueryKeys } from './attractionQueryKeys';
import { useCanonicalAttractionRedirect } from './useCanonicalAttractionRedirect';

/**
 * Resets the hero image to auto-selected.
 *
 * Same cache-buster strategy as useUpdateAttractionImage: the backend
 * returns updatedAt on DELETE, we persist it in sessionStorage so the next
 * GET appends ?v={updatedAt} and bypasses CloudFront. The auto-enrichment
 * happens server-side on reset, so the post-reset imageUrl comes from the
 * mutation response itself.
 */
export function useResetAttractionImage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { retargetFromMutationError } = useCanonicalAttractionRedirect();

  return useMutation({
    mutationFn: async (attractionId: string) => {
      return await resetAttractionImage(attractionId);
    },
    onMutate: async (attractionId) => {
      await queryClient.cancelQueries({ queryKey: attractionQueryKeys.detail(attractionId) });

      const previousAttraction = queryClient.getQueryData<Attraction>(attractionQueryKeys.detail(attractionId));

      if (previousAttraction) {
        queryClient.setQueryData<Attraction>(attractionQueryKeys.detail(attractionId), {
          ...previousAttraction,
          imageUrl: null,
        });
      }

      return { previousAttraction };
    },
    onSuccess: (data, attractionId) => {
      if (data.updatedAt) {
        setCacheBuster(attractionId, data.updatedAt);
      }

      queryClient.setQueryData<Attraction>(attractionQueryKeys.detail(attractionId), (prev) =>
        prev ? { ...prev, imageUrl: data.imageUrl } : prev,
      );

      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });

      enqueueSnackbar('Hero image reset to auto', { variant: 'success' });
    },
    onError: (error: Error, attractionId, context) => {
      if (retargetFromMutationError(error, attractionId)) return;
      if (context?.previousAttraction) {
        queryClient.setQueryData(attractionQueryKeys.detail(attractionId), context.previousAttraction);
      }
      enqueueSnackbar(`Error resetting image: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
